import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { POPUP_SECTIONS, offerTypeUsesRuntimeResolution, PRODUCT_TO_OFFER } from "../../../constant/constant";
import { getCoverBadgeDisplay } from "../../../../shared/product-block-display.js";
import { isSectionVisible } from "../../../../shared/section-visibility.js";
import { useApp } from "../../../context/AppState";
import { usePopup } from "../../../hooks/usePopup";
import useOfferProducts from "../../../hooks/useOfferProducts";
import CatalogProductOffer from "../../../components/Preview/catalog-product/CatalogProductOffer";
import CatalogProductOfferSkeleton from "../../../components/Preview/catalog-product/CatalogProductOfferSkeleton";
import {
  isPopupRuntimeContentLoading,
  resolveRuntimeProductSlotCount,
} from "../../../utils/popup-runtime-content-loading";
import styles from "./AiProductOfferSection.module.css";

function extractChrome(section, layoutOptions) {
  const sections = section?.sections || [];
  const coverBlock = sections.find((s) => s?.type === POPUP_SECTIONS.COVER_IMAGE);
  const badge = getCoverBadgeDisplay(coverBlock);

  return {
    showBestsellerBadge:
      badge.show !== false && layoutOptions.showBestsellerBadge !== false,
    bestsellerLabel: badge.label || layoutOptions.bestsellerLabel || "BESTSELLER",
  };
}

export default function AiProductOfferSection({
  layoutOptions,
  popupData,
  popupState,
  setPopupState,
  section,
  activeStep,
  handleClose,
  onProductsChange,
  isMobile: isMobileProp,
  isPreviewMode: isPreviewModeProp,
  contentLoading = false,
}) {
  const { isMobile: viewportIsMobile, appState } = useApp();
  const { isLoadingProducts, fetchRandomProduct } = usePopup();
  const isMobile =
    typeof isMobileProp === "boolean" ? isMobileProp : viewportIsMobile;
  const isPreviewMode =
    typeof isPreviewModeProp === "boolean"
      ? isPreviewModeProp
      : Boolean(appState?.is_preview_mode);

  const { products, isResolvingOfferProducts } = useOfferProducts({
    section,
    popupData,
    activeStep,
    isPreviewMode,
    isMobile,
    onProductsChange,
    fetchOfferProducts: fetchRandomProduct,
  });

  const productsToOfferType =
    popupData?.generalSettings?.condition?.product?.products_to_offer?.type;
  const usesRuntimeOffer = offerTypeUsesRuntimeResolution(productsToOfferType);

  const chrome = useMemo(
    () => extractChrome(section, layoutOptions),
    [section, layoutOptions]
  );

  const coverBlock = section?.sections?.find(
    (s) => s?.type === POPUP_SECTIONS.COVER_IMAGE
  );
  const showBadge =
    chrome.showBestsellerBadge &&
    chrome.bestsellerLabel &&
    (!coverBlock || isSectionVisible(coverBlock));
  const showRuntimeSkeleton = useMemo(() => {
    const runtimeLoading =
      contentLoading ||
      (!isPreviewMode && isPopupRuntimeContentLoading(popupData));
    const awaitingRuntimeProducts =
      !isPreviewMode && usesRuntimeOffer && products.length === 0;
    const awaitingSpecificProducts =
      !isPreviewMode &&
      productsToOfferType === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT &&
      isResolvingOfferProducts;

    if (isPreviewMode) {
      return (
        (isLoadingProducts || isResolvingOfferProducts) && products.length === 0
      );
    }

    return (
      isLoadingProducts ||
      isResolvingOfferProducts ||
      runtimeLoading ||
      awaitingRuntimeProducts ||
      awaitingSpecificProducts
    );
  }, [
    contentLoading,
    isLoadingProducts,
    isPreviewMode,
    isResolvingOfferProducts,
    popupData,
    products.length,
    productsToOfferType,
    usesRuntimeOffer,
  ]);
  const skeletonCount = resolveRuntimeProductSlotCount(
    popupData,
    activeStep,
    section
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.productCard}>
        {showBadge ? (
          <span className={styles.badge}>{chrome.bestsellerLabel}</span>
        ) : null}
        {showRuntimeSkeleton ? (
          <CatalogProductOfferSkeleton
            section={section}
            popupData={popupData}
            isMobile={isMobile}
            count={skeletonCount}
            layoutProfile="ai-upsell"
          />
        ) : (
          <CatalogProductOffer
          section={section}
          popupData={popupData}
          popupState={popupState}
          setPopupState={setPopupState}
          activeStep={activeStep}
          handleClose={handleClose}
          isMobile={isMobile}
          isPreviewMode={isPreviewMode}
          onProductsChange={onProductsChange}
          layoutProfile="ai-upsell"
          hideProductButton
          isLoading={isLoadingProducts}
        />
        )}
      </div>
    </div>
  );
}

AiProductOfferSection.propTypes = {
  layoutOptions: PropTypes.object,
  popupData: PropTypes.object,
  popupState: PropTypes.object,
  setPopupState: PropTypes.func,
  section: PropTypes.object,
  activeStep: PropTypes.number,
  handleClose: PropTypes.func,
  onProductsChange: PropTypes.func,
  isMobile: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  contentLoading: PropTypes.bool,
};
